import React, { useEffect, useRef, useState } from "react";
import { useGlobalState } from "./GlobalStateProvider";
import { playBGM, stopBGM } from "./SoundEffect";
import "./Entrance.css";
import FogEffectCanvas from "./FogEffectCanvas";
import { PASS } from "./NazoPhase";

const Entrance: React.FC = () => {
  const { isBusy, setBusy, setCurrentStep, ansArray, setAnsArray } = useGlobalState();
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [snatchName, setSnatchName] = useState(false);
  const [images, setImages] = useState<string[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [splitName, setSplitName] = useState<string[]>([]);
  const [exCount, setExCount] = useState<number>(0);
  const turbulenceRef = useRef<SVGFETurbulenceElement>(null);
  const turbulenceNameRef = useRef<SVGFETurbulenceElement>(null);
  const displacementRef = useRef<SVGFEDisplacementMapElement>(null);
  const inputYourNameRef = useRef<HTMLInputElement | null>(null);

  const to = "https://bad-assort.com/play/gothic-neue/fileList.php";

  useEffect(() => {
    try {
      const fetchImages = async () => {
        const response = await fetch(to);
        const data = await response.json();
        const imageArray: string[] = Object.values(data);
        setImages(imageArray);
      };
      fetchImages();
    } catch (error) {
      console.error('Error preloading images', error);
      alert("リソースの読み込みに失敗しました\n通信環境の良い場所でリトライしてください");
      setImagesLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (images.length === 0) {
      setImagesLoaded(true);
      return;
    }

    let loadedCount = 0;
    const imageElements = async () => {
      for (let i = 0; i < images.length; i++) {
        await new Promise<void>((resolve) => {
          const img = new Image();
          img.src = `${process.env.PUBLIC_URL}/img/${images[i]}`;
          img.onload = () => {
            loadedCount += 1;
            setCurrentImageIndex(() => loadedCount);
            if (loadedCount === images.length) {
              setImagesLoaded(true);
            }
            setTimeout(resolve, 50);
          };
        });
      }
    };

    imageElements().catch((error) => {
      console.error('Error preloading images', error);
      alert("リソースの読み込みに失敗しました\n通信環境の良い場所でリトライしてください");
      setImagesLoaded(true);
    });
  }, [images]);

  type JudgeNameRes = {
    "judge": "ok" | "ng",
    "denied"?: boolean,
  }

  const judgeName: () => Promise<JudgeNameRes> = async () => {
    const ur_name = inputYourNameRef.current?.value;
    if (ur_name) {
      let dat = { "name": ur_name };
      let response = await fetch(PASS, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dat)
      });
      let res = await response.json();
      return res;
    }
    return { "judge": "ng" };
  };

  const gameStart = async () => {
    const your_name = inputYourNameRef.current?.value;
    if (!your_name) return;
    const alert_box: HTMLElement | null = document.getElementById("name-alert");
    if (!alert_box) return;
    if (your_name.length < 2) {
      alert_box.innerText = "名前が短すぎます";
      return;
    }
    if (your_name.length > 12) {
      alert_box.innerText = "名前が長すぎます";
      return;
    }
    const res: JudgeNameRes = await judgeName();
    if (res.judge === "ng") {
      alert_box.innerText = "その名前は使用できません";
      return;
    }
    if (res.denied) {
      setCurrentStep(90);
      return;
    }
    alert_box.innerText = "";
    setBusy(true);
    const arr = [...ansArray];
    arr[7][0] = your_name;
    setAnsArray(arr);
    setSplitName(your_name.split(''));
    animateGlitch();
    playBGM("bg0");
    setTimeout(() => {
      setSnatchName(true);
      stopBGM();
    }, 2000);
    setTimeout(() => {
      playBGM("bg04");
    }, 4000);
    setTimeout(() => {
      setCurrentStep(10);
      stopBGM();
    }, 8000);
  };

  const imgPreload = <>
    <div style={{
      fontSize: "80%",
    }}>Now Loading... {currentImageIndex}/{images.length}</div>
    {images[currentImageIndex] && (
      <img style={{
        width: "1px",
        height: "1px",
      }} src={`./img/${images[currentImageIndex]}`} />
    )}
  </>;

  const inputNameDialog = <div id="input-name-dialog">
    <p onClick={() => setExCount(prev => prev + 1)}>あなたの名前を教えてください。</p>
    <p className="fc" style={{ color: "transparent" }}>tel i neym.</p>
    <p id="name-alert" style={{ color: "red", height: "1em" }}></p>
    <input type="text" id="your-name" ref={inputYourNameRef} disabled={isBusy ? true : false}></input>
    <div className="div-button" onClick={() => { exCount < 8 && gameStart(); }}>決定</div>
  </div>;

  const animateGlitch = () => {
    let animationFrameId: number;
    let startAnimate = new Date().getTime();

    const animate = () => {
      let newTime = new Date().getTime();
      const delta = newTime - startAnimate;
      if (turbulenceRef.current) {
        const freqX = 0;
        const freqY = Math.max(Math.cos(delta / 67) + Math.cos(delta / 247) - 1.5, (delta / 1000 - 1.85));
        if (delta > 2000) {
          turbulenceRef.current.setAttribute('baseFrequency', `0 0`);
          return;
        }
        turbulenceRef.current.setAttribute('baseFrequency', `${freqX} ${freqY}`);
        if (displacementRef.current) {
          displacementRef.current.setAttribute('in', freqY > 0 ? 'rgb-layers' : 'SourceGraphic');
        }
      }
      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => cancelAnimationFrame(animationFrameId);
  };

  const diveMyName = <>
    <p id="p-dive-name">
      {splitName.map((char, index) => (
        <span
          key={index}
          style={{
            transform: `translateX(${(index - splitName.length / 2 + 0.5) * 2}em) translateY(-${(index * 19 % splitName.length) / splitName.length}em)`,
            animation: `2.5s dive-char-pre ${(index * 13 % splitName.length) / splitName.length * 0.8}s forwards, 2s dive-char-post ${(index * 23 % splitName.length) / splitName.length + 3.3}s forwards`
          }}
        >
          <span
            style={{
              display: "inline-block",
              transformOrigin: "center center",
              rotate: `${Math.sin(((new Date().getMilliseconds() + index) ** 2) % 100) * 30}deg`
            }}>
            {char}
          </span>
        </span>
      ))}
    </p>
    <svg xmlns="http://www.w3.org/2000/svg" style={{ display: "none" }}>
      <filter id="noisyFilter">
        <feTurbulence type="turbulence" ref={turbulenceNameRef} baseFrequency="0.2 0.4" numOctaves="1" result="turb" />
        <feColorMatrix
          in="turb"
          type="matrix"
          values="
            1 -1 0 0 0.5
            0 1 -1 0 0.5
            -1 0 1 0 0.5
            0 0 0 1 1"
          result="centered"
        />
        <feDisplacementMap in2="centered" in="SourceGraphic"
          scale="2"
          xChannelSelector="R"
          yChannelSelector="G"
        />
      </filter>
    </svg>
  </>;

  useEffect(() => {
    if (!snatchName) return;
    let animationFrameId: number;
    let startAnimate = new Date().getTime();

    const animate = () => {
      let newTime = new Date().getTime();
      const delta = newTime - startAnimate;
      if (turbulenceNameRef.current) {
        const freqX = Math.sin(delta) + 1;
        const freqY = Math.cos(delta) + 1;
        turbulenceNameRef.current.setAttribute('baseFrequency', `${freqX} ${freqY}`);
      }
      animationFrameId = requestAnimationFrame(animate);
    };

    animate();
    animateWave();

    return () => cancelAnimationFrame(animationFrameId);
  }, [snatchName]);


  const animateWave = () => {
    let animationFrameId: number;
    let startAnimate = new Date().getTime();

    const animate = () => {
      let newTime = new Date().getTime();
      const delta = newTime - startAnimate;
      if (turbulenceRef.current) {
        const freqX = Math.sin(delta / 40000) + 1.1;
        const freqY = Math.cos(delta / 40000) + 1.2;
        turbulenceRef.current.setAttribute('baseFrequency', `${freqX * 0.01} ${freqY * 0.01}`);
      }
      if (displacementRef.current) {
        displacementRef.current.setAttribute('scale', `${(delta / 700) ** 1.5}`);
      }
      animationFrameId = requestAnimationFrame(animate);
    };

    animate();
    return () => cancelAnimationFrame(animationFrameId);
  };

  const extraEntranceModal = <>
    <div id="extra-modal">
      <p>ここから先は有料コンテンツになります。</p>
      <p>進む前に、以下の注意事項をご一読ください。</p>
      <ul>
        <li>事前に「Gothic:Neue おまけコンテンツ」のアクティベーションコードをご購入いただき、専用のページからアンロックしてください。アンロックされていない場合、データの読み込みができません。</li>
        <li>これ以降の場面は、不特定多数に向けての公開や配信利用は禁止です。</li>
        <li>ヒント、解答、解説は一切ありません。また今後の公開も予定しておりません。</li>
      </ul>
      <div>
        <div className="div-button" onClick={() => setCurrentStep(101)}>理解した</div>
        <div className="div-button" onClick={() => setExCount(0)}>戻る</div>
      </div>
    </div>
  </>;

  return <>
    <section id="pregame" className={snatchName ? "effect" : ""}>
      <FogEffectCanvas snatchName={snatchName} />
      <div id="pregame-main">
        <img id="amnesia" src={`${process.env.PUBLIC_URL}/img/entrance.webp`} style={{ position: "absolute", width: "100%", height: "100%", maxHeight: "150vw", objectFit: "cover", zIndex: "-1" }}></img>
        {!imagesLoaded && imgPreload}
        {imagesLoaded && !snatchName && inputNameDialog}
        {snatchName && diveMyName}
      </div>
      <svg xmlns="http://www.w3.org/2000/svg" style={{ display: "none" }}>
        <filter id="displacementFilter">
          <feColorMatrix in="SourceGraphic" type="matrix" values="0 0 0 0 0 0 1 0 0 0 0 0 0 0 0 0 0 0 1 0" result="g" />
          <feColorMatrix in="SourceGraphic" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 1 0 0 0 0 0 1 0" result="b" />
          <feColorMatrix in="SourceGraphic" type="matrix" values="1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 1 0" />
          <feOffset dx="5" dy="1" />
          <feGaussianBlur stdDeviation="2" />
          <feComposite in="g" in2="" operator="lighter" />
          <feComposite in="b" in2="" operator="lighter" result="rgb-layers" />
          <feTurbulence ref={turbulenceRef} type="turbulence" baseFrequency="0 0" numOctaves="1" result="turb" />
          <feColorMatrix
            in="turb"
            type="matrix"
            values="
            1 -1 0 0 0.5
            0 1 -1 0 0.5
            -1 0 1 0 0.5
            0 0 0 1 1"
            result="centered"
          />
          <feDisplacementMap ref={displacementRef} in2="centered" in="SourceGraphic"
            scale="20"
            xChannelSelector="R"
            yChannelSelector="G"
          />
        </filter>
      </svg>
      {exCount > 7 && extraEntranceModal}
    </section>
  </>;
};

export default Entrance;
